import { ArrowLeftOutlined, FolderOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
	getFile,
	getFilePreview,
	getFolderPath,
} from "../services/api-server/welldocs";
import { createWellExaminationApi } from "../services/api-server/wellExamination";
import {
	documentsOwnerFromRoute,
	resolveDocumentsRoot,
} from "../services/documentsRoot";
import Emitter from "../utils/emitter";
import { folderLink } from "../utils/sharepointLinks";
import { HomeContext } from "./Home";
import NoAccess from "./NoAccess";
import PdfViewer from "../components/PDF/CustomPdfViewer";
import DefaultContainer from "../components/DefaultContainer";

type PreviewMode = "well-examination";

type LocationCrumb = { key: string; label: string; to?: string };

const decodePathSegment = (segment: string | undefined): string | null => {
	if (!segment) return null;
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
};

/**
 * Fallback location when the documents root can't be resolved: the folder
 * names in a Graph `parentReference.path` below its first two segments
 * (<project>/<operation>). Wells and operations created before June 2025 keep
 * their folder at the drive root, so for those it drops a real folder.
 */
const foldersFromParentPath = (parentPath: string | undefined): string[] => {
	const rootIndex = parentPath?.indexOf("root:") ?? -1;
	if (!parentPath || rootIndex === -1) return [];
	return parentPath
		.slice(rootIndex + "root:".length)
		.split("/")
		.filter(Boolean)
		.slice(2);
};

const SharepointPreview = ({ mode }: { mode?: PreviewMode }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const params = useParams();
	const context: any = useContext(HomeContext);
	const tenantKey = context.currentTenantKey;
	const searchParams = new URLSearchParams(location.search);
	const file_id = searchParams.get("record");
	const container_id = searchParams.get("container");
	// Page the preview belongs to: its route minus "/preview".
	const pagePath = location.pathname.substring(
		0,
		location.pathname.lastIndexOf("/")
	);
	// Well examination previews live at /projects/<code>/well-examination/preview
	const projectCode =
		mode === "well-examination"
			? decodePathSegment(location.pathname.split("/")[2])
			: null;
	// Whose documents browser ("Contents") the breadcrumb starts from.
	const documentsOwner =
		mode === "well-examination"
			? null
			: documentsOwnerFromRoute(params, location.pathname);

	// Where the file metadata, preview link and download URL come from.
	const source = useMemo(() => {
		if (!tenantKey || !file_id) return null;
		if (mode === "well-examination") {
			if (!projectCode) return null;
			const api = createWellExaminationApi(tenantKey, projectCode);
			return {
				getFile: () => api.getFile(file_id, container_id || ""),
				getPreview: () => api.getPreview(file_id, container_id || ""),
				getDownloadUrl: (item: any) =>
					api.getDownloadUrl(item, container_id || ""),
			};
		}
		return {
			getFile: () => getFile(tenantKey, file_id, container_id),
			getPreview: () => getFilePreview(tenantKey, file_id, container_id),
			getDownloadUrl: async (item: any) =>
				item?.["@microsoft.graph.downloadUrl"] || null,
		};
	}, [mode, tenantKey, projectCode, file_id, container_id]);

	const goBack = () => {
		if (mode === "well-examination" && projectCode) {
			navigate(`/projects/${projectCode}`);
			return;
		}
		navigate(pagePath);
	};

	const [previewUrl, setPreviewUrl] = useState<any>(null);
	const [restricted, setRestricted] = useState<boolean>(false);
	const [isPdf, setIsPdf] = useState<boolean>(false);
	const [fileName, setFileName] = useState<string>("");
	// Folders parsed from the SharePoint path; shown when the lookup below has no answer.
	const [pathFolders, setPathFolders] = useState<string[]>([]);

	// Where the file sits below its page's documents root, resolved by id.
	const folderPathQuery = useQuery({
		queryKey: ["preview-folder-path", tenantKey, documentsOwner, file_id],
		queryFn: async () => {
			if (!documentsOwner || !file_id) return null;
			const root = await resolveDocumentsRoot(tenantKey, documentsOwner);
			return root
				? getFolderPath(root.tenantKey, file_id, root.folderId)
				: null;
		},
		enabled: !!tenantKey && !!file_id && !!documentsOwner,
		retry: false,
		// Previews open in their own browser tab; don't re-walk Graph on every
		// focus. Reopening the preview still fetches afresh.
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		Emitter.emit("loading", {
			name: "sharepoint-preview",
			isLoading: true,
		});
		if (source) {
			source.getFile().then(async (response: any) => {
				setFileName(response?.name ?? "");
				setPathFolders(foldersFromParentPath(response?.parentReference?.path));

				if (response.file.mimeType != "application/pdf") {
					source.getPreview().then((response: any) => {
						setPreviewUrl(response?.getUrl);
						setIsPdf(false);
						Emitter.emit("loading", {
							name: "sharepoint-preview",
							isLoading: false,
						});
					});
				} else {
					const downloadUrl = await source.getDownloadUrl(response);
					if (downloadUrl) {
						const fileResponse = await axios.get(downloadUrl, {
							responseType: "arraybuffer",
						});
						const file = URL.createObjectURL(new Blob([fileResponse.data]));
						setPreviewUrl(file);
						setIsPdf(true);
					} else {
						setRestricted(true);
					}
					Emitter.emit("loading", {
						name: "sharepoint-preview",
						isLoading: false,
					});
				}
			});
			// getFilePreview(context.currentTenantKey, file_id, container_id).then(
			// 	(response: any) => {
			// 		setPreviewUrl(response?.getUrl);
			// 		console.log(response);
			// 		Emitter.emit("loading", {
			// 			name: "sharepoint-preview",
			// 			isLoading: false,
			// 		});
			// 	}
			// );

			// let well_id: any = location?.pathname?.split("/")[2];
			// getWells(context.currentTenantKey, { _id: well_id }).then(
			// 	(wells: any) => {
			// 		if (wells && wells.length > 0) {
			// 			context.setCurrentWell(wells[0]);
			// 			getFilePreview(
			// 				context.currentTenantKey,
			// 				file_id,
			// 				container_id
			// 			).then((response: any) => {
			// 				setPreviewUrl(response?.getUrl);
			// 				Emitter.emit("loading", {
			// 					name: "sharepoint-preview",
			// 					isLoading: false,
			// 				});
			// 			});
			// 		} else {
			// 			setRestricted(true);
			// 		}
			// 	}
			// );
		}
	}, [source]);

	const locationCrumbs = (): LocationCrumb[] => {
		// Show nothing while resolving rather than flash the fallback.
		if (folderPathQuery.isLoading) return [];
		const folders = folderPathQuery.data?.folders;
		if (!folders) {
			return pathFolders.map((name, index) => ({
				key: `${index}`,
				label: name,
			}));
		}
		return [
			{ key: "contents", label: "Contents", to: folderLink(pagePath, []) },
			...folders.map((folder, index) => ({
				key: folder.id,
				label: folder.name,
				to: folderLink(
					pagePath,
					folders.slice(0, index + 1).map((ancestor) => ancestor.id)
				),
			})),
		];
	};

	const breadcrumbRenderer = () => {
		const crumbs = locationCrumbs();
		return (
			<div className="flex items-center gap-3">
				<Button
					type="link"
					icon={<ArrowLeftOutlined />}
					onClick={() => {
						goBack();
					}}
					style={{ flexShrink: 0 }}
				>
					Back
				</Button>
				{fileName && <span className="font-bold">{fileName}</span>}
				{crumbs.length > 0 && (
					<div className={`flex items-center text-white/60!`}>
						<span className="mr-2">Located in:</span>
						{crumbs.map((crumb, index) => {
							const label =
								index === crumbs.length - 1 ? (
									<span
										className={`flex gap-2 px-2 rounded-xs items-center border border-white/40`}
									>
										<FolderOutlined />
										<span>{crumb.label}</span>
									</span>
								) : (
									<span>{crumb.label}</span>
								);
							return (
								<span key={crumb.key} className="flex items-center">
									{index > 0 && <span className="mx-2">&gt;</span>}
									{crumb.to ? <Link to={crumb.to}>{label}</Link> : label}
								</span>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	return (
		<div className="h-full w-full">
			{restricted ? (
				<>
					<NoAccess
						break={true}
						text={
							"Oops, looks like you don't have the authorisation to view this page."
						}
					/>
				</>
			) : (
				previewUrl && (
					<>
						{isPdf ? (
							<PdfViewer
								fileUrl={previewUrl}
								open={true}
								title={fileName}
								onClose={() => {
									goBack();
								}}
								toolbarLeft={breadcrumbRenderer()}
							/>
						) : (
							<DefaultContainer headerContent={breadcrumbRenderer()}>
								<iframe
									src={previewUrl}
									width="100%"
									height="100%"
									style={{ border: "none" }}
									title="SharePoint Document Preview"
								/>
							</DefaultContainer>
						)}
					</>
				)
			)}
		</div>
	);
};

export default SharepointPreview;
